import { isRecord } from '@apps/web/components/phoebe_chat/record_utils';
import { type StreamRunState } from '@apps/web/components/phoebe_chat/streaming_types';
import {
    type RunTraceEntry,
    type ToolExecutionEntry,
    type UpdatePlaybookArtifact,
    type UpdatePlaybookArtifactEntry,
} from '@apps/web/components/phoebe_chat_view/transcript_types';

const RUNTIME_CONTEXT_ITEM_TYPE = 'runtime_context';

export function isRuntimeContextRawJson(value: unknown): boolean {
    return isRecord(value) && value['type'] === RUNTIME_CONTEXT_ITEM_TYPE;
}

const TOOL_RULES_PREVIEW_CHAR_LIMIT = 700;

export function stripRulesDocumentHeading(markdown: string): string {
    return markdown.replace(/^#\s+.*\n*/, '').trim();
}

function getUpdatePlaybookArtifact(
    argumentsValue: unknown,
    outputValue: unknown,
    textValue: string | null = null,
): UpdatePlaybookArtifact | null {
    if (!isRecord(argumentsValue) && !isRecord(outputValue) && !textValue) {
        return null;
    }

    const args = isRecord(argumentsValue) ? argumentsValue : null;
    const output = isRecord(outputValue) ? outputValue : null;

    const updatedRulesMarkdown =
        args && typeof args['updated_rules_markdown'] === 'string'
            ? args['updated_rules_markdown'].trim()
            : null;
    const message =
        output && typeof output['message'] === 'string'
            ? output['message']
            : textValue;

    if (!updatedRulesMarkdown && !message) {
        return null;
    }

    return {
        updatedRulesMarkdown,
        message,
    };
}

export function buildRulesMarkdownPreview(markdown: string): string {
    const normalized = stripRulesDocumentHeading(markdown);
    if (normalized.length === 0) {
        return '';
    }

    const collapsedWhitespace = normalized.replace(/\n{3,}/g, '\n\n').trim();
    const lines = collapsedWhitespace.split('\n');
    let lastHeadingIndex = -1;

    for (let index = lines.length - 1; index >= 0; index -= 1) {
        const line = lines[index];
        if (line && /^#{2,6}\s+/.test(line.trim())) {
            lastHeadingIndex = index;
            break;
        }
    }

    const sectionLines =
        lastHeadingIndex >= 0 ? lines.slice(lastHeadingIndex) : lines;
    const sectionPreview = sectionLines.join('\n').trim();
    if (sectionPreview.length <= TOOL_RULES_PREVIEW_CHAR_LIMIT) {
        return sectionPreview;
    }

    if (lastHeadingIndex >= 0 && sectionLines.length > 1) {
        const heading = sectionLines[0];
        if (!heading) {
            return `…${sectionPreview
                .slice(-TOOL_RULES_PREVIEW_CHAR_LIMIT)
                .trimStart()}`;
        }
        const body = sectionLines.slice(1).join('\n').trim();
        const remainingCharBudget = Math.max(
            TOOL_RULES_PREVIEW_CHAR_LIMIT - heading.length - 2,
            0,
        );
        if (remainingCharBudget > 0) {
            return `${heading}\n…${body
                .slice(-remainingCharBudget)
                .trimStart()}`;
        }
    }

    return `…${sectionPreview
        .slice(-TOOL_RULES_PREVIEW_CHAR_LIMIT)
        .trimStart()}`;
}

export function collectUpdatePlaybookArtifacts(
    entries: Array<RunTraceEntry>,
): Array<UpdatePlaybookArtifactEntry> {
    const toolEntries: Array<ToolExecutionEntry> = [];

    for (const entry of entries) {
        if (entry.kind === 'tool-execution') {
            toolEntries.push(entry);
            continue;
        }

        if (entry.kind === 'thought') {
            toolEntries.push(...entry.tools);
        }
    }

    return toolEntries.flatMap((toolEntry) => {
        if (
            (toolEntry.name !== 'update_playbook' &&
                toolEntry.name !== 'update_callout_rules') ||
            !toolEntry.isComplete
        ) {
            return [];
        }

        const artifact = getUpdatePlaybookArtifact(
            toolEntry.arguments,
            toolEntry.output,
            toolEntry.text,
        );

        if (!artifact) {
            return [];
        }

        return [
            {
                kind: 'update-playbook-artifact',
                id: `update-playbook-artifact-${toolEntry.id}`,
                createdAt: toolEntry.createdAt,
                artifact,
                turnSequence: toolEntry.turnSequence,
            },
        ];
    });
}

export function collectUpdatePlaybookArtifactsFromRun(
    run: StreamRunState | null,
): Array<UpdatePlaybookArtifactEntry> {
    if (!run) {
        return [];
    }

    return run.orderedNodeIds.flatMap((nodeId) => {
        const node = run.nodesById[nodeId];
        if (
            !node ||
            node.kind !== 'tool' ||
            (node.toolName !== 'update_playbook' &&
                node.toolName !== 'update_callout_rules') ||
            !node.isComplete
        ) {
            return [];
        }

        const textValue =
            typeof node.result === 'string'
                ? node.result
                : (node.error ?? null);
        const outputValue =
            typeof node.result === 'string' ? null : node.result;
        const artifact = getUpdatePlaybookArtifact(
            node.arguments,
            outputValue,
            textValue,
        );
        if (!artifact) {
            return [];
        }

        return [
            {
                kind: 'update-playbook-artifact',
                id: `live-update-playbook-artifact-${node.id}`,
                createdAt: node.createdAt,
                artifact,
            },
        ];
    });
}
